import { OfferType } from '@/types';
import { WLAS_PARAMS } from './constants';
import { MAX_FILTERS_RANGE_VALUES } from '@/constants/maxFilterRangeValues';

export type WlasValues = Record<string, string | boolean>;

export interface WlasFilters {
  offerTypes: OfferType[] | null;
  categories: string[] | null;
  unitListValue: string[] | null;
  priceValue: number[];
  rentPriceValue: number[];
  areaValue: number[];
  roomsValue: number[];
  equipment: string[] | null;
  powerConsumptionClasses: string[] | null;
  energyClassFactors: string[] | null;
}

export function defaultValues(): WlasValues {
  return Object.fromEntries(
    WLAS_PARAMS.filter((p) => !p.readonly).map((p) => [p.id, p.defaultVal]),
  );
}

export function defaultFilters(): WlasFilters {
  return {
    offerTypes: null,
    categories: null,
    unitListValue: null,
    priceValue: [0, MAX_FILTERS_RANGE_VALUES.SELL_PRICE],
    rentPriceValue: [0, MAX_FILTERS_RANGE_VALUES.RENT_PRICE],
    areaValue: [0, MAX_FILTERS_RANGE_VALUES.AREA],
    roomsValue: [0, MAX_FILTERS_RANGE_VALUES.ROOMS],
    equipment: null,
    powerConsumptionClasses: null,
    energyClassFactors: null,
  };
}

/**
 * Returns true if localParams differ from savedParams in any field.
 */
export function isDirtyCheck(saved: WlasValues, local: WlasValues): boolean {
  const keys = Object.keys(saved);
  return keys.some((k) => saved[k] !== local[k]);
}

/**
 * Returns true if localFilters differ from savedFilters.
 */
export function isDirtyFiltersCheck(saved: WlasFilters, local: WlasFilters): boolean {
  const arrayKeys: (keyof WlasFilters)[] = [
    'offerTypes',
    'categories',
    'unitListValue',
    'equipment',
    'powerConsumptionClasses',
    'energyClassFactors',
    'priceValue',
    'rentPriceValue',
    'areaValue',
    'roomsValue',
  ];
  for (const key of arrayKeys) {
    const a = saved[key] as (string | number)[] | null;
    const b = local[key] as (string | number)[] | null;
    if (a === null && b === null) continue;
    if (a === null || b === null) return true;
    if (a.length !== b.length || a.some((v, i) => v !== b[i])) return true;
  }
  return false;
}

/**
 * Build widget attributes from values.
 * When `onlyNonDefault` is true, params at their default value are omitted.
 * `member-unique-code` is always included via the separate `memberUniqueCode` argument.
 */
export function buildAttrs(
  memberUniqueCode: string,
  values: WlasValues,
  onlyNonDefault = false,
): Record<string, string> {
  const attrs: Record<string, string> = {
    'member-unique-code': memberUniqueCode,
  };

  for (const param of WLAS_PARAMS) {
    if (param.readonly) continue;

    const raw = values[param.id];
    const value = param.type === 'bool' ? (raw ? 'true' : 'false') : String(raw ?? '');

    if (param.skipEmpty && !value) continue;

    const isAtDefault =
      param.type === 'bool' ? raw === param.defaultVal : value === String(param.defaultVal);

    if (onlyNonDefault && isAtDefault && !param.alwaysInclude) continue;

    attrs[param.attr] = value;
  }

  return attrs;
}

function escapeAttrValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function buildSnippet(attrs: Record<string, string>, scriptSrc: string): string {
  const attrLines = Object.entries(attrs)
    .map(([k, v]) => `  ${k}="${escapeAttrValue(v)}"`)
    .join('\n');
  return `<script src="${scriptSrc}" defer></script>\n\n<alleimmobilien-wlas-widget\n${attrLines}\n/>`;
}
